import { useEffect, useMemo, useRef, useState } from "react";
import type { HTMLInputTypeAttribute } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  getLegalCertificateProgress,
  getLegalCertificateStampPaperPreview,
  LegalCertificateWorkflowStep,
  saveLegalCertificateStampPaper,
  sendLegalCertificateStampPaperWhatsApp,
  type LegalCertificateStampPaperPreview,
  type LegalCertificateStampVendorOption,
  type SaveLegalCertificateStampPaperPayload,
} from "../../api/legalCertificate";
import { getStampVendors, type StampVendorOption } from "../../api/discussion";

export const LEGAL_CERTIFICATE_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID =
  "legal-certificate-stamp-paper-details-to-vendor-form";

const STAMP_PAPER_COUNT_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

const APPLICANT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
];

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = APPLICANT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
}

function formatUserDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return value;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return value;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  return `${day} ${month} ${d.getFullYear()}`;
}

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

type FieldErrors = {
  stampVendorName?: string;
  stampVendorPhone?: string;
};

function validateVendorFields(
  name: string,
  phone: string
): { ok: true } | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {};
  if (!name.trim()) errors.stampVendorName = "Enter the stamp vendor name";
  const trimmedPhone = phone.trim();
  if (!trimmedPhone) {
    errors.stampVendorPhone = "Enter the stamp vendor phone number";
  } else {
    const digits = trimmedPhone.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      errors.stampVendorPhone = "Enter a valid phone number";
    }
  }
  if (errors.stampVendorName || errors.stampVendorPhone) {
    return { ok: false, errors };
  }
  return { ok: true };
}

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  type = "text",
  inputMode,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
      </label>
      <div
        className={`${INPUT_BASE} ${
          errorText ? "border-red-400 focus-within:ring-red-400" : ""
        }`}
      >
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`min-w-0 flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
      {errorText ? (
        <p className="text-[11px] leading-4 text-red-600" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

function FetchedDetailsPanel({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              value ? "text-[#222222]" : "text-[#6D6D6D]"
            }`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function StampCountSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
      </label>
      <Select
        value={String(value ?? 0)}
        onValueChange={(v) => {
          const parsed = parseInt(v, 10);
          onChange(Number.isFinite(parsed) ? parsed : 0);
        }}
      >
        <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {STAMP_PAPER_COUNT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function LegalCertificateStampPaperDetailsToVendorView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const navigate = useNavigate();

  // Read-only applicant block (from Discussion Step 2 — Application Details)
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState<string | null>(null);
  const [userDate, setUserDate] = useState<string | null>(null);
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");

  // Editable
  const [poaStamps, setPoaStamps] = useState(0);
  const [stampVendorName, setStampVendorName] = useState("");
  const [stampVendorPhone, setStampVendorPhone] = useState("");
  const [stampVendors, setStampVendors] = useState<
    LegalCertificateStampVendorOption[]
  >([]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [previewMessage, setPreviewMessage] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const applyPreview = (data: LegalCertificateStampPaperPreview) => {
    setApplicant(data.applicantName ?? "");
    setApplicantAddress(data.applicantAddress ?? "");
    setApplicantType(data.applicantType ?? null);
    setUserDate(data.userDate ?? null);
    setSignatoryName(data.signatoryName ?? "");
    setSignatoryDesignation(data.signatoryDesignation ?? "");
    setPoaStamps(data.poaStamps ?? 0);
    setStampVendorName(data.stampVendorName ?? "");
    setStampVendorPhone(data.stampVendorPhone ?? "");
    if (Array.isArray(data.stampVendors) && data.stampVendors.length > 0) {
      setStampVendors(data.stampVendors);
    }
    setPreviewMessage(data.message ?? null);
  };

  // Initial load: preview + saved stamp vendors list
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getLegalCertificateStampPaperPreview(leadId),
      getStampVendors().catch((): StampVendorOption[] => []),
    ])
      .then(([preview, vendors]) => {
        if (cancelled) return;
        applyPreview(preview);
        if (Array.isArray(vendors) && vendors.length > 0) {
          setStampVendors(vendors);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load stamp paper details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Workflow progress — is this step already completed (sent)?
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getLegalCertificateProgress(leadId);
        const step = progress.steps.find(
          (s) =>
            s.slug ===
            LegalCertificateWorkflowStep.LC_S2_01_StampPaperDetailsToVendor
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  const buildPayload = (): SaveLegalCertificateStampPaperPayload => ({
    poaStamps,
    stampVendorName: stampVendorName.trim() || undefined,
    stampVendorPhone: stampVendorPhone.trim() || undefined,
  });

  // Debounced auto-save so the WhatsApp preview always reflects the latest input.
  useEffect(() => {
    if (!leadId || loading) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveLegalCertificateStampPaper(leadId, buildPayload())
        .then((data) => {
          setPreviewMessage(data.message ?? null);
        })
        .catch(() => {
          // Auto-save errors stay silent; explicit submit surfaces real issues.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, poaStamps, stampVendorName, stampVendorPhone]);

  const handleVendorSelect = (value: string) => {
    if (value === "__none__") {
      setStampVendorName("");
      setStampVendorPhone("");
      setFieldErrors({});
      return;
    }
    setStampVendorName(value);
    const vendor = stampVendors.find((v) => v.name === value);
    setStampVendorPhone(vendor?.phone ?? "");
    setFieldErrors({});
  };

  const vendorSelectValue = useMemo(() => {
    return stampVendorName &&
      stampVendors.some((v) => v.name === stampVendorName)
      ? stampVendorName
      : "__none__";
  }, [stampVendorName, stampVendors]);

  const persistAndRefresh = async (currentLeadId: string) => {
    const data = await saveLegalCertificateStampPaper(
      currentLeadId,
      buildPayload()
    );
    applyPreview(data);
    return data;
  };

  const validate = (): { ok: boolean; firstError?: string } => {
    const validation = validateVendorFields(stampVendorName, stampVendorPhone);
    if ("errors" in validation) {
      setFieldErrors(validation.errors);
      const firstError =
        validation.errors.stampVendorName ??
        validation.errors.stampVendorPhone;
      return { ok: false, firstError };
    }
    setFieldErrors({});
    if (poaStamps < 1) {
      return {
        ok: false,
        firstError: "Number of Stamp Papers for PoA must be at least 1.",
      };
    }
    return { ok: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validation = validate();
    if (!validation.ok) {
      if (validation.firstError) toast.error(validation.firstError);
      return;
    }
    setSaving(true);
    try {
      await persistAndRefresh(leadId);
      if (hasSentWhatsapp) {
        toast.success("Stamp paper details to vendor saved successfully.");
        onSuccess?.(leadId);
      } else {
        setConfirmMode("send");
        setShowConfirmModal(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    (async () => {
      try {
        await sendLegalCertificateStampPaperWhatsApp(leadId);
        setHasSentWhatsapp(true);
        onSentStatusChange?.(true);
        toast.success(
          confirmMode === "resend"
            ? "Stamp paper details resent to vendor via WhatsApp."
            : "Stamp paper details sent to vendor successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || resending || saving) return;
    const validation = validate();
    if (!validation.ok) {
      if (validation.firstError) toast.error(validation.firstError);
      return;
    }
    setResending(true);
    try {
      await persistAndRefresh(leadId);
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading stamp paper details...</p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <h3 className="text-sm font-medium text-[#222222]">
        Stamp Paper details to Vendor for Legal Certificate
      </h3>

      {/* ─────────────────────────────────────────────────────────────────
          Section 1 — Editable: stamp count and vendor.
          ─────────────────────────────────────────────────────────────── */}
      <h4 className="text-xs font-medium leading-5 text-[#222222]">
        Stamp Vendor Details
      </h4>

      {/* Number of Stamp Papers for PoA | Name of Stamp Vendor (quick-select) */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <StampCountSelect
          label="Number of Stamp Papers for PoA"
          value={poaStamps}
          onChange={(v) => setPoaStamps(v)}
        />
        <div className="flex flex-1 min-w-0 flex-col gap-1 w-full">
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Name of Stamp Vendor
          </label>
          <Select value={vendorSelectValue} onValueChange={handleVendorSelect}>
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select to autofill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Add new vendor —</SelectItem>
              {stampVendors
                .filter(
                  (v) => typeof v.name === "string" && v.name.trim().length > 0
                )
                .map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name}
                    {v.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Editable Name + Phone — always visible, autofilled by the dropdown,
          but the user can edit them or enter a brand-new vendor. */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Name of Stamp Vendor"
          value={stampVendorName}
          onChange={(v) => {
            setStampVendorName(v);
            setFieldErrors((prev) => ({
              ...prev,
              stampVendorName: undefined,
            }));
          }}
          placeholder="Vendor name"
          errorText={fieldErrors.stampVendorName}
        />
        <TextField
          label="Phone of Stamp Vendor"
          value={stampVendorPhone}
          onChange={(v) => {
            setStampVendorPhone(v);
            setFieldErrors((prev) => ({
              ...prev,
              stampVendorPhone: undefined,
            }));
          }}
          placeholder="Phone number"
          type="tel"
          inputMode="tel"
          errorText={fieldErrors.stampVendorPhone}
        />
      </div>

      <div className="border-t border-[#EEE] my-1" />

      {/* ─────────────────────────────────────────────────────────────────
          Section 2 — Fetched application details (read-only)
          Sourced from Discussion Step 2 ▸ Application Details from Client.
          ─────────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
          Application Details (from previous step)
        </h4>
        <button
          type="button"
          onClick={() =>
            navigate(
              `/dashboard/legal-certificate/new/discussion/application-details-from-client/${leadId}`
            )
          }
          className="text-xs font-medium leading-5 text-[#222222] underline decoration-[#222222] underline-offset-2 hover:opacity-80"
        >
          Edit
        </button>
      </div>

      <FetchedDetailsPanel
        rows={[
          {
            label: "Applicant Type",
            value: formatApplicantTypeLabel(applicantType),
          },
          { label: "Signatory's Designation", value: signatoryDesignation },
          { label: "Applicant", value: applicant },
          { label: "Applicant Address", value: applicantAddress },
          { label: "Signatory's Name", value: signatoryName },
          { label: "User Date", value: formatUserDate(userDate) },
        ]}
      />

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewMessage || "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!previewMessage?.trim()}
      />

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            disabled={resending || saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}
